import type { Mutation, QueryClient } from '@tanstack/react-query';
import type { Currency } from '../constants';
import type { GetCartResponse } from '../infrastructure/cart/utils/types';
import { CART_QUERY_KEYS } from '../infrastructure/cart/utils/keys';
import { filterSystemCoupons } from './cart/cartSummary';

interface ReferralCouponContext {
  currency: Currency;
  authenticated: boolean;
  code: string;
  replacedCodes?: readonly string[];
}

export function getCartEligibleForReferralCoupon(
  queryClient: QueryClient,
  queryKey: ReturnType<typeof CART_QUERY_KEYS.getCart>,
  expected: ReferralCouponContext,
) {
  if (
    queryClient.isMutating({ mutationKey: CART_QUERY_KEYS.default() }) > 0 ||
    queryClient.isFetching({ queryKey: CART_QUERY_KEYS.default() }) > 0
  ) {
    return undefined;
  }
  const state = queryClient.getQueryState<GetCartResponse>(queryKey);
  const cart = state?.data;
  if (
    state?.status !== 'success' ||
    state.fetchStatus !== 'idle' ||
    !cart?.cartInfo.cartToken ||
    cart.isAuthenticated !== expected.authenticated ||
    cart.cart.currency.currency !== expected.currency
  ) {
    return undefined;
  }
  if (
    cart.cart.item.length === 0 ||
    filterSystemCoupons(cart.cart.coupons).some(
      (coupon) => !coupon.autoApplied && !expected.replacedCodes?.includes(coupon.code.toUpperCase()),
    ) ||
    cart.cart.coupons.some((coupon) => coupon.code.toUpperCase() === expected.code.toUpperCase())
  ) {
    return undefined;
  }
  return cart;
}

export function observeReferralCouponRemovalIntent(
  queryClient: QueryClient,
  {
    code,
    currency,
    onRemove,
    ignoredRemovals,
  }: { code: string; currency: Currency; onRemove: (request: object) => void; ignoredRemovals?: WeakSet<object> },
) {
  const key = CART_QUERY_KEYS.removeCoupon();
  const seen = new WeakSet<Mutation>();
  const inspect = (mutation: Mutation) => {
    const variables = mutation.state.variables;
    if (
      seen.has(mutation) ||
      mutation.state.status !== 'pending' ||
      mutation.options.mutationKey?.length !== key.length ||
      !key.every((part, index) => mutation.options.mutationKey?.[index] === part) ||
      !variables ||
      typeof variables !== 'object' ||
      ignoredRemovals?.has(variables) ||
      !('code' in variables) ||
      typeof variables.code !== 'string' ||
      variables.code.toUpperCase() !== code.toUpperCase() ||
      !('currency' in variables) ||
      variables.currency !== currency
    ) {
      return;
    }
    seen.add(mutation);
    onRemove(variables);
  };
  const cache = queryClient.getMutationCache();
  const unsubscribe = cache.subscribe((event) => {
    if (event.type === 'updated' && event.action.type === 'pending') {
      inspect(event.mutation);
    }
  });
  cache.getAll().forEach(inspect);
  return unsubscribe;
}
